import { Controller } from '@hotwired/stimulus';

/**
 * Timer - counts up from `startedAt` every second and writes the clock into the clock target. With `tab`, the
 * browser tab shows it too: the clock before the page title and a red dot on the favicon, both put back when
 * the timer goes away (stopped, or another page). Every tick dispatches `timer:tick` with the seconds, which
 * live-totals adds to the totals that include the running entry.
 * Values:  startedAt (String, ISO 8601) | format ("long" 01:24:07, "short" 1:24) | tab (Boolean)
 * Targets: clock
 * Events:  timer:tick ({ seconds })
 * @example <span data-controller="timer" data-timer-started-at-value="2026-09-24T14:08:00Z" data-timer-tab-value="true"><span data-timer-target="clock"></span></span>
 */
export default class extends Controller {
  static targets = ['clock'];
  static values = { startedAt: String, format: { type: String, default: 'long' }, tab: Boolean };

  connect () {
    if (this.tabValue) this.#markTab();
    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
  }

  disconnect () {
    clearInterval(this.interval);
    if (this.tabValue) this.#unmarkTab();
  }

  tick () {
    if (!this.startedAtValue || !this.hasClockTarget) return;
    const seconds = Math.max(0, Math.floor((Date.now() - Date.parse(this.startedAtValue)) / 1000));
    this.clockTarget.textContent = this.#format(seconds);
    this.dispatch('tick', { detail: { seconds } });
    if (this.tabValue) {
      this.tabTitle = `${this.#format(seconds, 'long')} · ${this.pageTitle}`;
      document.title = this.tabTitle;
    }
  }

  #format (seconds, format = this.formatValue) {
    const pad = (n) => String(n).padStart(2, '0');
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return format === 'short' ? `${h}:${pad(m)}` : `${pad(h)}:${pad(m)}:${pad(s)}`;
  }

  // The favicon with a red dot in its corner, drawn over the app icon.
  #markTab () {
    this.pageTitle = document.title;
    this.icons = [...document.querySelectorAll('link[rel="icon"]')].map((link) => ({ link, href: link.href, type: link.type }));
    const image = new Image();
    image.onload = () => {
      if (!this.tabValue || !this.element.isConnected) return;

      const canvas = document.createElement('canvas');
      canvas.width = 64;
      canvas.height = 64;
      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0, 64, 64);
      context.beginPath();
      context.arc(51, 13, 11, 0, 2 * Math.PI);
      context.fillStyle = '#E5322B';
      context.fill();
      context.lineWidth = 3;
      context.strokeStyle = '#FFFFFF';
      context.stroke();
      const dotted = canvas.toDataURL('image/png');
      this.icons.forEach(({ link }) => { link.type = 'image/png'; link.href = dotted; });
    };
    image.src = '/icon.png';
  }

  // Back to the plain favicon and title, unless the next page already put its own title.
  #unmarkTab () {
    (this.icons || []).forEach(({ link, href, type }) => { link.href = href; link.type = type; });
    if (document.title === this.tabTitle) document.title = this.pageTitle;
  }
}
